/**
 * OrbitSpinner — Constellation Orbit, the primary/general-purpose spinner in
 * Piano Fantasy's loading vocabulary (brand-kit v1.87, §20). Three gold notes
 * orbiting a glowing core, driven by `var(--gold)` (or `var(--gold-on-cream)`
 * via the `cream` prop) so it reads correctly on this site's dark AND cream
 * section backgrounds — pianofantasy.com has no light/dark theme toggle,
 * it alternates section background color instead.
 *
 * Ported 1:1 from the same canonical source used on app.pianofantasy.com
 * (spinner-preview.html + brand-kit §20) — identical geometry, durations,
 * and easing. CSS lives in app/globals.css under the "§20 LOADING
 * VOCABULARY" block (`.lv-orbit` / `.lv-orbit-16/-36/-56` / `.lv-orbit-slow`).
 *
 * Size variants, sized to the moment:
 *   16  — inline next to button/form text ("Signing you up…", "Sending…")
 *   36  — a section or inline content load (the default)
 *   56  — a full-page load
 *
 * `patient` slows the orbit ~1.5x for a genuinely long wait — pair with a
 * caption that updates over time. Not currently used on this site (no wait
 * long enough to warrant it was found in the 2026-07-25 audit).
 */
export interface OrbitSpinnerProps {
  size?: 16 | 36 | 56;
  /** Use the cream-surface gold variant (`--gold-on-cream` + higher-alpha track) instead of the dark-surface default. */
  cream?: boolean;
  /** Slows the orbit for a "this is genuinely going to take a while" moment. */
  patient?: boolean;
  className?: string;
  'aria-label'?: string;
  /**
   * When the spinner sits beside adjacent visible text that already
   * communicates the loading state (e.g. a parent `role="status"
   * aria-label="Thinking"` bubble), render it purely decorative
   * (aria-hidden, no role) instead of adding a second, redundant
   * status-region announcement. Default false (spinner is its own
   * announced status region).
   */
  decorative?: boolean;
}

export default function OrbitSpinner({
  size = 36,
  cream = false,
  patient = false,
  className,
  'aria-label': ariaLabel = 'Loading',
  decorative = false,
}: OrbitSpinnerProps) {
  const sizeClass = size === 16 ? 'lv-orbit-16' : size === 56 ? 'lv-orbit-56' : 'lv-orbit-36';
  const classes = [
    'lv-orbit',
    sizeClass,
    cream ? 'cream' : null,
    patient ? 'lv-orbit-slow' : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const a11yProps = decorative
    ? { 'aria-hidden': true as const }
    : { role: 'status' as const, 'aria-label': ariaLabel };

  return (
    <span className={classes} {...a11yProps}>
      <span className="lv-ring" aria-hidden="true" />
      <span className="lv-core" aria-hidden="true" />
      <span className="lv-dot lv-dot-1" aria-hidden="true" />
      <span className="lv-dot lv-dot-2" aria-hidden="true" />
      <span className="lv-dot lv-dot-3" aria-hidden="true" />
    </span>
  );
}
